import { usePreferencesProvider } from "@jugl-web/domain-resources/preferences";
import { useUserEntityPreferences } from "@jugl-web/domain-resources/preferences/hooks/useUserEntityPreferences";
import { ChatType, SoundSupportedModule } from "@jugl-web/rest-api";
import { getUniqueId, trimHTML, useTabIsActive } from "@jugl-web/utils";
import useEntity from "@web-src/features/app/hooks/useEntity";
import { selectUserId } from "@web-src/features/auth/authSlice";
import { extractNumberFromSoundType } from "@web-src/modules/settings/pages/SettingsPage/components/NotificationsSettingsSidebar/utils/extractNumberFromSoundType";
import { useSnackbar } from "notistack";
import { FC, PropsWithChildren, useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { MENTIONS_ALL_ID } from "@jugl-web/utils/consts";
import { environment } from "@web-src/environments/environment";
import { useAnonymousSession } from "@web-src/modules/auth/providers/AnonymousSessionProvider";
import { FCMProvider } from "../FCMProvider";
import { NotificationsPermissionsPopup } from "./components/NotificationsPermissionsPopup/NotificationsPermissionsPopup";
import {
  InAppNotification,
  NotificationsContext,
  NotificationsContextValue,
  TriggerNotificationParams,
} from "./NotificationsContext";

const MAIN_TAB_STORAGE_KEY = "JUGL:NOTIFICATIONS_MAIN_TAB";

const isMainTab = (tabKey?: string) =>
  tabKey && localStorage.getItem(MAIN_TAB_STORAGE_KEY) === tabKey;

export const NotificationsProvider: FC<PropsWithChildren> = ({ children }) => {
  const [tabKey, setTabKey] = useState<string>();
  const tabIsActive = useTabIsActive();

  useEffect(() => {
    if (!tabIsActive) {
      return;
    }
    const key = getUniqueId();
    localStorage.setItem(MAIN_TAB_STORAGE_KEY, key);
    setTabKey(key);
  }, [tabIsActive]);

  const [canPlaySound, setCanPlaySound] = useState<boolean>(false);
  useEffect(() => {
    const mouseOverCallback = () => {
      setCanPlaySound(true);
      document.body.removeEventListener("click", mouseOverCallback);
    };
    document.body.addEventListener("click", mouseOverCallback);
  }, []);

  const { enqueueSnackbar } = useSnackbar();
  const meId = useSelector(selectUserId);
  const [shownNotifications, setShownNotifications] = useState<{
    [key: string]: boolean;
  }>({});
  const location = useLocation();
  const { preferences: appPreferences } = usePreferencesProvider();

  // Notification status and popup is now managed in NotificationsPermissionsPopup component

  const { entity } = useEntity();
  const { isAnonymous } = useAnonymousSession();
  const { preferences: userPreferences, updatePreferences } =
    useUserEntityPreferences({
      entityId: entity?.id,
      skip: isAnonymous,
    });
  const notificationPreferences = useMemo(
    () => userPreferences?.notification_pref,
    [userPreferences]
  );

  const triggerPushNotification = useCallback(
    (title: string, params?: TriggerNotificationParams) => {
      if (document.visibilityState === "visible") {
        return;
      }

      // Check if notifications are supported and permitted
      if (
        !("Notification" in window) ||
        Notification.permission !== "granted"
      ) {
        return;
      }

      // eslint-disable-next-line no-new
      const notification = new Notification(title, {
        body: params?.body,
        tag: params?.tag,
      });
      notification.onclick = () => {
        window.focus();
        params?.onClick?.();
      };
    },
    []
  );

  const playNotificationSoundForModule = useCallback(
    (type?: SoundSupportedModule) => {
      if (
        (type && userPreferences?.notification_pref?.[type]?.sound === false) ||
        !canPlaySound
      ) {
        return;
      }
      const notificationSoundId = type
        ? extractNumberFromSoundType(
            userPreferences?.notification_pref?.[type]?.sound_web
          ) ?? 0
        : 0;
      new Audio(
        `/notification-sounds/notification${notificationSoundId || ""}.mp3`
      ).play();
    },
    [canPlaySound, userPreferences]
  );

  const getChatNotificationsPreferences = useCallback<
    NotificationsContextValue["getChatNotificationsPreferences"]
  >(
    (chatId) =>
      notificationPreferences?.chats?.except?.[chatId]?.mute ||
      notificationPreferences?.groups?.except?.[chatId]?.mute ||
      "none",
    [notificationPreferences]
  );

  const isNotificationInSameModule = useCallback(
    (notification: InAppNotification) => {
      if (notification.module === "chats" || notification.module === "groups") {
        return location.pathname.includes("/chats");
      }
      if (notification.module === "drive") {
        return location.pathname.includes("/drive");
      }
      if (notification.module === "task") {
        // TODO: add entityId to notification
        return (
          location.pathname.includes("/tasks") &&
          !location.pathname.includes("/reports/tasks")
        );
      }
      if (notification.module === "crm") {
        return location.pathname.includes("/client-support");
      }
      return false;
    },
    [location.pathname]
  );

  const isChatNotificationMuted = useCallback(
    (chatId: string, body: string) => {
      const chatNotificationPreferences =
        getChatNotificationsPreferences(chatId);
      if (chatNotificationPreferences === "all") {
        return true;
      }
      if (chatNotificationPreferences === "all_except_mentions") {
        return (
          (!meId || !body.includes(meId)) && !body.includes(MENTIONS_ALL_ID)
        );
      }
      return false;
    },
    [getChatNotificationsPreferences, meId]
  );

  const triggerInAppNotification = useCallback(
    (notification: InAppNotification) => {
      if (notification.key) {
        if (shownNotifications[notification.key]) {
          return;
        }
        setShownNotifications({
          ...shownNotifications,
          [notification.key]: true,
        });
      }
      // TODO: revisit
      if (
        notification.module &&
        userPreferences?.notification_pref?.[notification.module]?.noti ===
          false
      ) {
        return;
      }
      if (
        (notification.module === "chats" || notification.module === "groups") &&
        notification.senderId &&
        isChatNotificationMuted(
          notification.senderId,
          notification.clearBody || ""
        )
      ) {
        return;
      }
      if (!appPreferences.inAppNotificationsEnabled) {
        return;
      }
      const isInSameModule = isNotificationInSameModule(notification);
      if ((document.hidden || !isInSameModule) && isMainTab(tabKey)) {
        playNotificationSoundForModule?.(notification.module);
      }
      if (document.hidden && isMainTab(tabKey)) {
        triggerPushNotification?.(notification.title, {
          body: trimHTML(notification.body || ""),
          onClick: notification.onClick,
        });
      }
      if (!isInSameModule) {
        enqueueSnackbar({
          key: "1",
          variant: "inAppNotification",
          notification,
          anchorOrigin: {
            horizontal: "right",
            vertical: "top",
          },
          autoHideDuration: 5000,
          transitionDuration: 300,
        });
      }
    },
    [
      userPreferences?.notification_pref,
      isChatNotificationMuted,
      appPreferences.inAppNotificationsEnabled,
      isNotificationInSameModule,
      shownNotifications,
      playNotificationSoundForModule,
      triggerPushNotification,
      enqueueSnackbar,
      tabKey,
    ]
  );

  // TODO: move out to user preferences module
  const setChatNotificationsPreferences = useCallback<
    NotificationsContextValue["setChatNotificationsPreferences"]
  >(
    (chatId, chatType, option) => {
      if (!entity?.id) {
        return;
      }
      updatePreferences({
        notification_pref:
          chatType === ChatType.muc
            ? { groups: { except: { [chatId]: { mute: option } } } }
            : { chats: { except: { [chatId]: { mute: option } } } },
      });
    },
    [entity, updatePreferences]
  );

  const contextValue = useMemo<NotificationsContextValue>(
    () => ({
      triggerInAppNotification,
      getChatNotificationsPreferences,
      setChatNotificationsPreferences,
      notificationPreferences,
    }),
    [
      triggerInAppNotification,
      getChatNotificationsPreferences,
      setChatNotificationsPreferences,
      notificationPreferences,
    ]
  );

  return (
    <NotificationsContext.Provider value={contextValue}>
      {entity && !environment.isDemo && !isAnonymous && (
        <NotificationsPermissionsPopup />
      )}
      <FCMProvider>{children}</FCMProvider>
    </NotificationsContext.Provider>
  );
};
